import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DeviceTokenRequest, ENVIRONMENT, LoansData, UserTopic, UserTopicsRequest } from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';

import { LoggingService } from './logging.service';

@Injectable({ providedIn: 'root' })
export class PushNotificationApiService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly logger = inject(LoggingService);

  private static readonly TAG = 'PushNotificationApi';

  private logError(method: string, message: string, error?: unknown): void {
    this.logger.logException(
      error instanceof Error ? error : new Error(`[${PushNotificationApiService.TAG}] ${method}: ${message}`),
    );
  }

  private get baseUrl(): string {
    return this.env.appSettings.profileDomainUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  async upsertUserDevice(globalUserId: string, request: DeviceTokenRequest): Promise<boolean> {
    const url = `${this.baseUrl}/${this.apiNamespace}/LDProfileDomain/PushNotification/UpsertUserDevice/${globalUserId}`;
    try {
      await firstValueFrom(this.http.post(url, request));
      return true;
    } catch (error) {
      this.logError('UpsertUserDevice', 'failed', error);
      return false;
    }
  }

  async upsertUserTopicSubscriptions(userProfileId: string, request: UserTopicsRequest): Promise<boolean> {
    const url = `${this.baseUrl}/${this.apiNamespace}/LDProfileDomain/PushNotification/UpsertUserTopicSubscriptions/${userProfileId}`;
    try {
      await firstValueFrom(this.http.post(url, request));
      return true;
    } catch (error) {
      this.logError('UpsertUserTopicSubscriptions', 'failed', error);
      return false;
    }
  }

  async getUserTopicSubscriptions(userProfileId: string, loans: LoansData[]): Promise<UserTopic[]> {
    const url = `${this.baseUrl}/${this.apiNamespace}/LDProfileDomain/PushNotification/GetUserTopicSubscriptionsDetails/${userProfileId}`;
    try {
      const response = await firstValueFrom(this.http.post<UserTopic[]>(url, loans));
      return response ?? [];
    } catch (error) {
      this.logError('GetUserTopicSubscriptions', 'failed', error);
      return [];
    }
  }

  async getTopics(): Promise<UserTopic[]> {
    const url = `${this.baseUrl}/${this.apiNamespace}/LDProfileDomain/PushNotification/GetTopics`;
    try {
      const response = await firstValueFrom(this.http.get<UserTopic[]>(url));
      return response ?? [];
    } catch (error) {
      this.logError('GetTopics', 'failed', error);
      return [];
    }
  }
}
